// Input — styled text input with optional error state + leftIcon slot.
//
// - `error` renders a red border via `data-invalid="true"` (per spec §3).
// - `leftIcon` wraps the input in a flex row with consistent icon inset.
// - Focus ring bound to `--color-ring`.

import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from './utils/cn.js';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  leftIcon?: ReactNode;
}

const baseClasses = [
  'flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground',
  'placeholder:text-muted-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-50',
  'data-[invalid=true]:border-destructive data-[invalid=true]:focus-visible:ring-destructive',
].join(' ');

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, error, leftIcon, type = 'text', ...props },
  ref,
) {
  if (leftIcon) {
    return (
      <div
        className={cn(
          'relative flex w-full items-center',
          // Pass through disabled opacity if the wrapped input is disabled.
        )}
      >
        <span
          className="pointer-events-none absolute left-3 flex h-4 w-4 items-center justify-center text-muted-foreground"
          aria-hidden="true"
        >
          {leftIcon}
        </span>
        <input
          ref={ref}
          type={type}
          data-invalid={error ? 'true' : undefined}
          className={cn(baseClasses, 'pl-9', className)}
          {...props}
        />
      </div>
    );
  }

  return (
    <input
      ref={ref}
      type={type}
      data-invalid={error ? 'true' : undefined}
      className={cn(baseClasses, className)}
      {...props}
    />
  );
});
